import * as React from 'react';
import {
  AuthUserService,
  AuthUserSubcode,
  AuthUser,
  AuthRoles,
} from '@brandbank/portal-components';
import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react';

export type OmittedInterface = Omit<Auth0ContextInterface, 'user'>;

export interface UserContextInterface extends OmittedInterface {
  appLogin: (redirectUri: string) => void;
  hasRole(role: AuthRoles, subcode?: string): boolean;
  roleChecks: {
    canOnlyViewDocumentation: boolean;
  } | null;
  token: string;
  user: AuthUser | null;
}

const useUser = (): UserContextInterface => {
  const [token, setToken] = React.useState('');
  const [user, setUser] = React.useState<AuthUser | null>(null);

  const hasRole = (role: AuthRoles, subcode?: string): boolean => {
    if (!user) return false;

    if (subcode) {
      // subcode given, find subcode and check for role
      const foundSubcode = user.roles.subcodes.find(
        (item) => item.subcode === subcode
      );

      if (foundSubcode && foundSubcode.roles.indexOf(role) > -1) {
        return true;
      }
    } else if (
      user.roles.subcodes.some((item) => item.roles.indexOf(role) > -1)
    ) {
      // No subcode given, check ALL user subcodes for role match
      return true;
    }

    // Fallback to check service roles if subcode and/or role was not found
    return user.roles.serviceRoles.indexOf(role) > -1;
  };

  const {
    user: auth0User,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    ...options
  } = useAuth0();

  const appLogin = (redirectUri: string): void => {
    const urlHost = window.location.host;
    const urlScheme = urlHost.indexOf('localhost') > -1 ? 'http' : 'https';

    options.loginWithRedirect({
      redirectUri: `${urlScheme}://${urlHost}${redirectUri}`,
    });
  };

  const roleChecks = React.useMemo(() => {
    if (isAuthenticated && user) {
      const onlyHasDocumentationRole = (roles: AuthRoles[]): boolean =>
        roles.length === 1 && roles.indexOf('Data Model Viewer') > -1;

      const { serviceRoles, subcodes } = user.roles;

      const canOnlyViewDocumentationServiceLevel =
        serviceRoles.length === 0 || onlyHasDocumentationRole(serviceRoles);

      const canOnlyViewDocumentationSubcodeLevel = subcodes.every((item) =>
        onlyHasDocumentationRole(item.roles)
      );

      return {
        canOnlyViewDocumentation:
          canOnlyViewDocumentationServiceLevel &&
          canOnlyViewDocumentationSubcodeLevel,
      };
    }

    return null;
  }, [isAuthenticated, user]);

  // Get token
  React.useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((tkn) => {
        localStorage.setItem('token', tkn);
        setToken(tkn);
      });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Create User
  React.useEffect(() => {
    if (!auth0User) {
      setUser(null);
    } else {
      const parsedServices: AuthUserService[] = JSON.parse(
        auth0User['https://auth.brandbank.com/services']
      );

      const { firstName, lastName } = JSON.parse(
        auth0User['https://auth.brandbank.com/user']
      );

      const serviceRoles = parsedServices.length
        ? parsedServices[0].serviceRoles || []
        : [];

      const subcodesToMap = parsedServices.length
        ? parsedServices[0].subcodes || []
        : [];

      const subcodes: AuthUserSubcode[] = subcodesToMap.map((item) => ({
        subcode: item.subcode,
        roles: item.roles || [],
      }));

      setUser({
        email: auth0User.email || '',
        name: `${firstName} ${lastName}`,
        roles: {
          serviceRoles,
          subcodes,
        },
        picture: auth0User.picture || '',
      });
    }
  }, [auth0User]);

  return {
    appLogin,
    hasRole,
    roleChecks,
    user,
    token,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    ...options,
  };
};

export default useUser;
